import * as React from "react";
import {BooleanRadioButton, BooleanRadioButtonGroup, Checkbox} from "@octopusdeploy/design-system-components";
import repository from "client/repository";
import {
    DataTable,
    DataTableBody,
    DataTableRow,
    DataTableRowColumn,
    DataTableRowHeaderColumn,
    DataTableRowWithActionColumn
} from "components/DataTable";
import {DataBaseComponent, DataBaseComponentState} from "components/DataBaseComponent";
import {HostedInstanceResource} from "client/resources/hostedInstanceResource";
import {HostedInstanceUsageMetricsResource} from "client/resources/hostedInstanceUsageMetricsResource";
import {TaskLimitUsageResource} from "client/resources/taskLimitUsageResource";
import PaperLayout from "components/PaperLayout";
import {default as OverflowMenu} from "components/Menu/OverflowMenu";
import SaveDialogLayout from "components/DialogLayout/SaveDialogLayout";
import {Note, Text} from "components/form";
import {linkToInstance} from "areas/instances/linkToInstance";
import UnstructuredFormSection from "components/form/Sections/UnstructuredFormSection";
import FormSectionHeading from "components/form/Sections/FormSectionHeading";
import ExternalLink from "components/Navigation/ExternalLink/ExternalLink";
import ActionList from "components/ActionList/ActionList";
import {InstanceLimitResource} from "client/resources/instanceLimitResource";
import externalSystemLinks from "externalSystemLinks";
import TimePicker from "components/form/DatePicker/TimePicker";
import {HostedInstanceOutageWindowRequest} from "client/resources/hostedInstanceOutageWindowRequest";
import {DatabaseRestoreRequest} from "client/resources/databaseRestoreRequest";
import {InternalRedirect} from "components/Navigation/InternalRedirect";
import Select from "components/form/Select/Select";
import {ReefResource} from "client/resources/reefResource";
import TaskSnackbar from "shared/TaskSnackbar";
import routeLinks from "routeLinks";
import {LocalTime} from "shared/localTime";
import {TakeProcessDumpDialog} from "areas/instances/Instances/TakeProcessDumpDialog";
import {CollectDotnetTraceDialog} from "areas/instances/Instances/CollectDotnetTraceDialog";
import {ReprovisionDialog} from "areas/instances/Instances/ReprovisionDialog";
import {UpgradeDialog} from "areas/instances/Instances/UpgradeDialog";
import {MoveFileStorageDialog} from "areas/instances/Instances/MoveFileStorageDialog";
import {MoveComputeDialog} from "areas/instances/Instances/MoveComputeDialog";
import {RestoreFileShareDialog} from "areas/instances/Instances/RestoreFileShareDialog";
import {RestartConfirmDialog} from "areas/instances/Instances/RestartConfirmDialog";
import {RunSqlDialog} from "./RunSqlDialog";
import {HostedInstanceMoveReefRequest} from "client/resources/hostedInstanceMoveReefRequest";
import {ChangeStorageQuotaDialog} from "./ChangeStorageQuotaDialog";
import {CorrectInstanceVersionDialog} from "./CorrectInstanceVersionDialog";
import {auditLinks} from "areas/auditing/auditRouteLinks";
import {
    displayBetterUptimeStatus,
    displayInstanceStatus,
    displaySecondaryUptimeStatus
} from "areas/instances/Instances/displayInstanceStatus";
import OpenDialogButton from "components/Dialog/OpenDialogButton";
import moment from "moment";
import {ChangeUpgradeStrategyDialog} from "./ChangeUpgradeStrategyDialog";
import NextOutageWindowDisplay from "areas/instances/Instances/NextOutageWindowDisplay";
import OutageWindowDisplay from "areas/instances/Instances/OutageWindowDisplay";
import InternalLink from "components/Navigation/InternalLink/InternalLink";
import convertBooleanToText from "utils/convertBooleanToText";
import {HostedInstanceConfigureDatabaseRequest} from "../../../client/resources/hostedInstanceConfigureDatabaseRequest";
import {
    HostedInstanceAllowLocalHostOctopusPortalSignInRequest
} from "client/resources/HostedInstanceAllowLocalHostOctopusPortalSignInRequest";
import {
    HostedInstanceInstallLegacyCloudAuthProvidersRequest
} from "client/resources/HostedInstanceInstallLegacyCloudAuthProvidersRequest";
import { Callout } from "@octopusdeploy/design-system-components";
import {InstanceStatus} from "../../../client/resources/instanceStatus";
import {bytesToGigaBytes, gigaBytesToGigaBytes, megaBytesToGigaBytes} from "../../storageConversions";
import {UpdateMasterKeyDialog} from "./UpdateMasterKeyDialog";
import {
    isSpecifiedVersionUpgradeStrategyType,
    SpecifiedVersionUpgradeStrategy,
    TrackBranchUpgradeStrategy,
    UpgradeStrategyType,
    upgradeStrategyTypeDescriptions
} from "../../../client/resources/upgradeStrategy";
import {head} from "lodash";
import {HostedInstanceDatabaseScaleRequest} from "client/resources/HostedInstanceConfigureDatabaseScaleRequest";
import {notSet} from "./Instances";
import {InstanceTaskLockResource} from "client/resources/instanceTaskLockResource";
import {displayTaskStatus} from "areas/backgroundTasks/displayTaskStatus";
import {EditDatabaseMaxPoolSizeDialog} from "./EditDatabaseMaxPoolSizeDialog";
import {WarnToCancelViaBillingAlert, WarnToReactivateViaBillingAlert} from "./WarnToCancelViaBillingAlert";
import {ChangeDnsPrefixDialog} from "./ChangeDnsPrefixDialog";
import {InstanceEnvironmentVariableDetails} from "./InstanceEnvironmentVariableDetails";
import {InstanceAssignedEnvironmentVariableDetails} from "./InstanceAssignedEnvironmentVariableDetails";
import {
    ApplicableInstanceEnvironmentVariable,
    EnvironmentVariable
} from "client/repositories/instanceEnvironmentVariableRepository";
import {DeleteConfirmation} from "./DeleteConfirmation";
import {DowngradeDialog} from "./DowngradeDialog";
import {HostedInstanceDatabaseLimits} from "client/resources/hostedInstanceDatabaseLimits";
import {CleanUpStrategyRows} from "./CleanUpStrategyRows";
import {InstanceDetailLicenseXml} from "./InstanceDetailLicenseXml";
import {InstanceDetailNotes} from "./InstanceDetailNotes";
import {CleanUpStrategyType, isBranchInstance, LimitedLifetimeCleanUpStrategy} from "../../../client/resources/cleanUpStrategy";
import {RunDatabaseMaintenanceDialog} from "./RunDatabaseMaintenanceDialog";
import {OutageWindowFrequency, outageWindowFrequencyDescriptions} from "client/resources/outageWindowFrequency";
import {mapMaintenanceWindowToHostedInstanceOutageWindowRequest} from "client/resources/maintenanceWindow";
import {timeZoneSelectItems} from "./timeZones";
import {reefDisplayLabel} from "../../reefs/ReefDisplayLabel";
import {ConfigureInstanceLegacyReplicasDialog} from "./ConfigureInstanceLegacyReplicasDialog";
import {AreYouSureYouWantToIncreaseTheLimitCallout, ManualScalingCallout} from "./ManualScalingCallouts";
import {ConfigureLegacyComputeResourcesDialog} from "./ConfigureLegacyComputeResourcesDialog";
import {ConfigureModularExecutionsComputeResourcesDialog} from "./ConfigureModularExecutionsComputeResourcesDialog";
import {ConfigureInstanceExecutionsReplicasDialog} from "./ConfigureInstanceExecutionsReplicasDialog";
import {ConfigureRedisResourcesDialog} from "./ConfigureRedisResourcesDialog";
import {ConfigureHostingTaskLimitDialog} from "./ConfigureHostingTaskLimitDialog";
import {ZoneDiversityType} from "../../../client/resources/ZoneDiversityType";
import {AreYouReallySureConfirmation} from "./AreYouReallySureConfirmation";
import {EnvironmentName} from "client/resources/environmentName";
import {RetrieveSqlDeadlockGraphsDialog} from "./RetrieveSqlDeadlockGraphsDialog";
import {
    HostedInstanceConfigureDatabaseBackupRetentionRequest
} from "~/client/resources/HostedInstanceConfigureDatabaseBackupRetentionRequest";
import ProvisionOutboundPrivateLinkDialog from "./ProvisionOutboundPrivateLinkDialog";
import DeleteOutboundPrivateLinkDialog from "./DeleteOutboundPrivateLinkDialog";
import AddOutboundPrivateLinkDnsRecordDialog from "./AddOutboundPrivateLinkDnsRecordDialog";
import DeleteOutboundPrivateLinkDnsRecordDialog from "./DeleteOutboundPrivateLinkDnsRecordDialog";
import {ChangeBackupRetentionDialog} from "~/areas/instances/Instances/ChangeBackupRetentionDialog";
import NavigationButton, {NavigationButtonType} from "~/components/Button/NavigationButton";
import {css} from "@emotion/css";
import cn from "classnames";
import FormSectionHeadingWithAction from "~/shared/FormSectionHeadingWithAction/FormSectionHeadingWithAction";
import {HostedInstanceModularExecutionsResource} from "client/resources/hostedInstanceModularExecutionsResource";

const modularExecutionsVisibilityDimmed = css`
    opacity: 0.5;
    transition: opacity 0.2s ease-in-out;

    &:hover,
    &:focus,
    &:focus-within {
        opacity: 0.75;
    }
`;

interface State extends DataBaseComponentState {
    instance?: HostedInstanceResource;
    instanceLock?: InstanceTaskLockResource;
    instanceUsageMetrics?: HostedInstanceUsageMetricsResource;
    instanceLimit?: InstanceLimitResource;
    instanceEnvironmentVariables?: ApplicableInstanceEnvironmentVariable[];
    instanceAssignedEnvironmentVariables?: EnvironmentVariable[];
    databaseLimits?: HostedInstanceDatabaseLimits;
    reefs?: Map<string, ReefResource>;
    changeOutageWindowRequest: Partial<HostedInstanceOutageWindowRequest>;
    changeAllowLocalHostOctopusPortalSignInRequest: Partial<HostedInstanceAllowLocalHostOctopusPortalSignInRequest>;
    installLegacyCloudAuthProvidersRequest: Partial<HostedInstanceInstallLegacyCloudAuthProvidersRequest>;
    moveReefRequest: Partial<HostedInstanceMoveReefRequest>;
    restoreDatabaseRequest: Partial<DatabaseRestoreRequest>;
    configureDatabaseRequest: Partial<HostedInstanceConfigureDatabaseRequest>;
    databaseScaleRequest: Partial<HostedInstanceDatabaseScaleRequest>;
    databaseBackupRetentionRequest: Partial<HostedInstanceConfigureDatabaseBackupRetentionRequest>;
    redirectTo?: string;
    recentlyCreatedTask?: string;
    canDelete: boolean;
    canScaleToBusinessCritical: boolean;
    modularExecutionsFeatures: HostedInstanceModularExecutionsResource;
    environment: string;
}

interface Props {
    instanceId: string;
    breadcrumbTitle: string;
    breadcrumbChip: JSX.Element;
}

export class InstanceDetail extends DataBaseComponent<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            changeOutageWindowRequest: {},
            moveReefRequest: {},
            configureDatabaseRequest: {},
            databaseScaleRequest: {},
            restoreDatabaseRequest: {},
            databaseBackupRetentionRequest: {},
            canDelete: false,
            canScaleToBusinessCritical: false,
            modularExecutionsFeatures: null,
            changeAllowLocalHostOctopusPortalSignInRequest: {},
            installLegacyCloudAuthProvidersRequest: {},
            environment: ""
        };
    }

    async componentDidMount() {
        return await this.doBusyTask(async () => {
            const [instance, reefs] = await Promise.all([
                repository.HostedInstances.get(this.props.instanceId),
                repository.Reefs.map()
            ]);

            const [instanceUsageMetrics, databaseLimits, instanceLimit, paginatedLocks, instanceEnvironmentVariables, instanceAssignedEnvironmentVariables, modularExecutions] = await Promise.all([
                repository.HostedInstances.getUsage(instance.Id),
                repository.HostedInstances.getDatabaseLimits(instance.Id),
                repository.InstanceLimits.getForInstance(instance.Id),
                repository.HostedInstances.getInstanceTaskLocks({relatedToHostedInstanceIds: [instance.Id]}),
                repository.InstanceEnvironmentVariables.getInstanceEnvironmentVariableByHostedInstance(instance.Id),
                repository.InstanceEnvironmentVariables.getInstanceAssignedEnvironmentVariableByHostedInstance(instance.Id),
                repository.HostedInstances.getModularExecutions(instance.Id)
            ]);

            const instanceLock = head(paginatedLocks.Resources);
            const environment = (await repository.Configuration.getAppInfo()).Environment;

            this.setState({
                instance,
                instanceUsageMetrics,
                reefs,
                instanceLimit,
                instanceEnvironmentVariables,
                instanceAssignedEnvironmentVariables,
                databaseLimits,
                changeOutageWindowRequest: mapMaintenanceWindowToHostedInstanceOutageWindowRequest(instance.MaintenanceWindow),
                moveReefRequest: {
                    MoveDuringOutageWindow: true,
                    IncludeResources: true
                },
                configureDatabaseRequest: {
                    MaxSizeInMb: instance.DatabaseMaxSizeInMb,
                    RunDuringOutageWindow: true
                },
                restoreDatabaseRequest: {
                    PointInTime: moment.utc().toISOString()
                },
                databaseScaleRequest: {
                    RunDuringOutageWindow: true,
                    ServiceLevelObjective: instance.DatabaseServiceLevel
                },
                changeAllowLocalHostOctopusPortalSignInRequest: {
                    AllowLocalHostOctopusPortalSignIn: instance.AllowLocalHostOctopusPortalSignIn
                },
                installLegacyCloudAuthProvidersRequest: {
                    InstallLegacyCloudAuthProviders: this.isLegacyAuthProviders(instance.AuthProvidersToInstall),
                    ReprovisionDuringMaintenanceWindow: true
                },
                databaseBackupRetentionRequest: {
                    RetentionPeriod: instance.DatabaseBackupWeeklyLongTermRetention,
                    RunDuringOutageWindow: true
                },
                instanceLock,
                modularExecutionsFeatures: modularExecutions,
                environment
            });
        });
    }

    handleDatabaseRestoreDateInputChange = (event: React.FormEvent<HTMLInputElement>) => {
        const inputDate = new Date(event.currentTarget.value);

        const pointInTime = moment.utc(this.state.restoreDatabaseRequest.PointInTime);
        pointInTime.year(inputDate.getFullYear());
        pointInTime.month(inputDate.getMonth());
        pointInTime.date(inputDate.getDate());
        pointInTime.second(0);

        this.setState({restoreDatabaseRequest: {PointInTime: pointInTime.toISOString()}});
    };

    handleDatabaseRestoreTimeInputChange = (event: React.FormEvent<HTMLInputElement>) => {
        const indexOfFirst = event.currentTarget.value.indexOf(":");
        const hours = Number(event.currentTarget.value.substring(0, indexOfFirst));
        const minutes = Number(event.currentTarget.value.substring(indexOfFirst + 1));

        const pointInTime = moment.utc(this.state.restoreDatabaseRequest.PointInTime);
        pointInTime.hour(hours);
        pointInTime.minute(minutes);
        pointInTime.second(0);

        this.setState({restoreDatabaseRequest: {PointInTime: pointInTime.toISOString()}});
    };

    render() {
        if (this.state.redirectTo) {
            return <InternalRedirect to={this.state.redirectTo}/>;
        }

        const instance = this.state.instance;
        return <PaperLayout busy={this.state.busy}
                            errors={this.state.errors}
                            title="Detail"
                            breadcrumbTitle={this.props.breadcrumbTitle}
                            breadcrumbChip={this.props.breadcrumbChip}
                            sectionControl={<ActionList actions={instance ? this.getActions() : []}/>}>
            {instance && this.getBody()}
        </PaperLayout>;
    }

    private getActions() {
        const instance = this.state.instance;
        const instanceLimit = this.state.instanceLimit;
        const reefResource = this.getReefFor(instance);

        const menuItems = [
            this.canBeUpgraded(instance.Status) && OverflowMenu.dialogItem("Upgrade", this.upgradeDialog()),
            this.canBeReprovisioned(instance.Status) && OverflowMenu.dialogItem("Reprovision", this.reprovisioningDialog()),
            this.canBeReactivated(instance.Status) && OverflowMenu.dialogItem("Reconfigure Resources", this.reconfigureResourcesDialog()),
            this.canBeDowngraded(instance.Status) && OverflowMenu.dialogItem("Downgrade", this.downgradeDialog()),
            this.canBeReactivated(instance.Status) && OverflowMenu.dialogItem("Reactivate", this.reactivationDialog()),
            this.isNotDeleted(instance.Status) && OverflowMenu.dialogItem("Run SQL", this.runSqlDialog()),
            this.isNotDeleted(instance.Status) && OverflowMenu.dialogItem("Snapshot File Share", this.snapshotFileShareDialog()),
            this.canDataBeRestored(instance.Status) && OverflowMenu.dialogItem("Restore File Share", this.restoreFileShareDialog()),
            this.canDataBeRestored(instance.Status) && OverflowMenu.dialogItem("Restore Database", this.restoreDatabaseDialog()),
            this.isNotDeactivatedOrDeleted(instance.Status) && isBranchInstance(instance.CleanUpStrategy) && OverflowMenu.dialogItem("Add User As Admin", this.addUserAsAdminDialog()),
            this.isNotDeactivatedOrDeleted(instance.Status) && OverflowMenu.dialogItem("Take Process Dump", this.takeProcessDumpDialog()),
            this.isNotDeactivatedOrDeleted(instance.Status) && OverflowMenu.dialogItem("Collect .NET trace", this.collectDotnetTraceDialog()),
            this.isNotDeleted(instance.Status) && OverflowMenu.dialogItem("Retrieve SQL Deadlock Graphs", this.retrieveSqlDeadlockGraphs()),
            this.isNotDeactivatedOrDeleted(instance.Status) && OverflowMenu.dialogItem("Run Database Maintenance", this.runDatabaseMaintenanceDialog()),
            this.isNotDeactivatedOrDeleted(instance.Status) && OverflowMenu.dialogItem("Cancel All Database Maintenance Tasks", this.cancelDatabaseMaintenanceDialog()),
            this.isNotDeactivatedOrDeleted(instance.Status) && OverflowMenu.dialogItem("Revoke All Users Sessions", this.revokeUsersSessionsDialog()),
            this.isNotDeactivatedOrDeleted(instance.Status) && OverflowMenu.dialogItem("Restart", <RestartConfirmDialog errors={this.state.errors} busy={this.state.busy}
                                                                     instanceId={this.state.instance.Id}
                                                                     onDone={taskId => this.showNewTaskWindow(taskId)}/>),
            this.canBeDeactivated(instance.Status) && OverflowMenu.deleteItem("Deactivate",
                "Are you sure you want to deactivate this Instance?", () => this.handleDeactivateConfirm(),
                <WarnToCancelViaBillingAlert action="deactivate" instanceLimit={this.state.instanceLimit} />),
            this.isNotDeactivatedOrDeleted(instance.Status) && this.state.environment === "Development" && OverflowMenu.dialogItem("Enable Azure Authentication", this.azureAuthDialog()),
            this.canBeDeleted(instance.Status) && OverflowMenu.deleteItem("Delete", "Are you sure you want to delete this Instance?", () => this.handleDeleteConfirm(),
                <>
                    <WarnToCancelViaBillingAlert action="delete" instanceLimit={this.state.instanceLimit} />
                    <br/>
                    <DeleteConfirmation itemName={this.state.instance ? this.state.instance.DnsPrefix : ""}
                                        deleteWhat="Instance" onChange={canDelete => this.setState({canDelete})}/>
                </>,
                !this.state.canDelete),
            instance.BetterUptimeRawUptimeMonitorId && OverflowMenu.externalNavItem("Better Uptime (raw)",externalSystemLinks.betterUptime.rawUptimeLinks.status(instance)),
            OverflowMenu.externalNavItem("NGINX HTTP Logs", externalSystemLinks.sumoLogic.httpAccessLogs(instance)),
            OverflowMenu.externalNavItem("Istio HTTP Logs", externalSystemLinks.sumoLogic.istioHttpAccessLogs(instance)),
            OverflowMenu.externalNavItem("Polling Tentacle Connection Logs", externalSystemLinks.sumoLogic.pollingTentacleConnectionLogs(instance)),
            OverflowMenu.externalNavItem("Connection Relay Logs", externalSystemLinks.sumoLogic.connectionRelayLogs(instance)),
            reefResource && instance.HubTenantId && OverflowMenu.externalNavItem("Hub", externalSystemLinks.hub.root(reefResource)),
            instanceLimit?.CloudSubscriptionSerial && OverflowMenu.externalNavItem("Planhat", externalSystemLinks.planhat.assets(instanceLimit.CloudSubscriptionSerial)),
            OverflowMenu.navItem("Audit Trail", auditLinks.auditTrailFor(instance)),
            reefResource && reefResource.IsDedicated && OverflowMenu.dialogItem("Provision Outbound Private Link", this.outboundPrivateLinkDialog()),
            reefResource && reefResource.IsDedicated && OverflowMenu.dialogItem("Delete Outbound Private Link", this.deleteOutboundPrivateLinkDialog()),
            reefResource && reefResource.IsDedicated && OverflowMenu.dialogItem("Add Outbound Private Link DNS Record", this.addOutboundPrivateLinkDnsRecordDialog()),
            reefResource && reefResource.IsDedicated && OverflowMenu.dialogItem("Delete Outbound Private Link DNS Record", this.deleteOutboundPrivateLinkDnsRecordDialog()),
            this.canMasterKeyBeUpdated(instance.Status) && OverflowMenu.dialogItem("Update Master Key", <UpdateMasterKeyDialog instance={instance} />),

        ];

        return [
            instanceLimit.CloudSubscriptionId && <ExternalLink href={externalSystemLinks.billing.subscriptionDetails(instanceLimit)}>
                Subscription
            </ExternalLink>,
            reefResource && <ExternalLink
                href={externalSystemLinks.seq.instanceLogs(instance, reefResource)}>
                Logs (Seq)
            </ExternalLink>,
            <ExternalLink
                href={externalSystemLinks.sumoLogic.instanceLogs(instance)}>
                Logs (Sumo)
            </ExternalLink>,
            instance.BetterUptimeAlertingMonitorId && <ExternalLink href={externalSystemLinks.betterUptime.alertingLinks.status(instance)}>
                Uptime (Alerting)
            </ExternalLink>,
            reefResource && reefResource.SumoLogicInstanceDashboardId && <ExternalLink href={externalSystemLinks.sumoLogic.instanceMetrics(instance, reefResource)}>
                Metrics
            </ExternalLink>,
            <ExternalLink href={externalSystemLinks.honeycomb.instanceTraces(instance)}>
                Traces
            </ExternalLink>,
            <OverflowMenu menuItems={menuItems}/>
        ];
    }

    private canBeDeactivated(status: InstanceStatus) {
        return status !== InstanceStatus.Deleted && status !== InstanceStatus.Deleting && status !== InstanceStatus.Deactivated;
    }

    private canBeReactivated(status: InstanceStatus) {
        return status === InstanceStatus.Deactivated;
    }

    private canBeDeleted(status: InstanceStatus) {
        return status !== InstanceStatus.Deleted && status !== InstanceStatus.Deleting;
    }

    private canMasterKeyBeUpdated(status: InstanceStatus) {
        return status === InstanceStatus.Deactivated;
    }

    private canBeReprovisioned(status: InstanceStatus) {
        return status !== InstanceStatus.Deleted && status !== InstanceStatus.Deleting && status !== InstanceStatus.Deactivated;
    }

    private canBeUpgraded(status: InstanceStatus) {
        return status !== InstanceStatus.Deleted && status !== InstanceStatus.Deleting && status !== InstanceStatus.Deactivated;
    }

    private canBeDowngraded(status: InstanceStatus) {
        return status !== InstanceStatus.Deleted && status !== InstanceStatus.Deleting && status !== InstanceStatus.Deactivated;
    }

    private canDataBeRestored(status: InstanceStatus) {
        return status === InstanceStatus.Deactivated;
    }

    private canChangeDnsPrefix(status: InstanceStatus) {
        return ( status !== InstanceStatus.Deactivating &&
            status !== InstanceStatus.Deactivated &&
            status !== InstanceStatus.Deleting &&
            status !== InstanceStatus.Deleted );
    }

    private isNotDeleted(status: InstanceStatus) {
        return ( status !== InstanceStatus.Deleting &&
            status !== InstanceStatus.Deleted );
    }
    private isNotDeactivatedOrDeleted(status: InstanceStatus) {
        return ( status !== InstanceStatus.Deactivating &&
            status !== InstanceStatus.Deactivated &&
            status !== InstanceStatus.Deleting &&
            status !== InstanceStatus.Deleted );
    }

    

    private getBody() {
        const instance = this.state.instance;
        const fileUsage = this.state.instanceUsageMetrics && this.state.instanceUsageMetrics.Files;
        const executionsFileUsage = this.state.instanceUsageMetrics && this.state.instanceUsageMetrics.ExecutionsFiles;
        const databaseCpuUsage = this.state.instanceUsageMetrics && this.state.instanceUsageMetrics.DatabaseCpu;
        const podMemoryUsage = this.state.instanceUsageMetrics && this.state.instanceUsageMetrics.PodMemory;
        const cpuUsage = this.state.instanceUsageMetrics && this.state.instanceUsageMetrics.Cpu;
        const databaseLimits = this.state.databaseLimits;
        const isModularExecutionsEnabled = this.state.modularExecutionsFeatures?.IsEnabled;
        const isExecutionsHPAEnabled = this.state.modularExecutionsFeatures?.IsExecutionsHPAEnabled;
        const isExecutionsHPAScaleDownEnabled = this.state.modularExecutionsFeatures?.IsExecutionsHPAScaleDownEnabled;

        const reefResource = this.getReefFor(instance);

        return <div>
            <TaskSnackbar
                getLink={() => routeLinks.instances.instance(instance.Id).tasks}
                onRequestClose={() => this.setState({recentlyCreatedTask: null})}
                open={!!this.state.recentlyCreatedTask}/>
            {this.state.instanceLock && this.getLockWarning(this.state.instanceLock)}
            <FormSectionHeading title="Notes"/>
            <DataTable>
                <DataTableBody>
                    <InstanceDetailNotes instance={instance}
                                         setInstance={updated => this.setState({instance: updated})}
                                         errors={this.state.errors}
                                         busy={this.state.busy}
                                         doBusyTask={this.doBusyTask}/>
                </DataTableBody>
            </DataTable>
            <FormSectionHeading title="Ids"/>
            <DataTable>
                <DataTableBody>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Id</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{instance.Id}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Hub Tenant Id</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{instance.HubTenantId || "[Not Set]"}</DataTableRowColumn>
                    </DataTableRow>
                </DataTableBody>
            </DataTable>
            <FormSectionHeading title="Hosting"/>
            <DataTable>
                <DataTableBody>
                    {reefResource && <DataTableRow>
                        <DataTableRowHeaderColumn>Reef</DataTableRowHeaderColumn>
                        <DataTableRowWithActionColumn action={
                            <OpenDialogButton label="Move To Another Reef">
                                {this.moveToAnotherReef()}
                            </OpenDialogButton>}>
                            <InternalLink to={routeLinks.reefs.reef(reefResource.Id).root}>{reefDisplayLabel(reefResource)}</InternalLink>
                        </DataTableRowWithActionColumn>
                    </DataTableRow>}
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Kubernetes Cluster</DataTableRowHeaderColumn>
                        {instance.KubernetesClusterName ? (<DataTableRowWithActionColumn action={
                            <OpenDialogButton label="Move To Another Cluster">
                                <MoveComputeDialog instance={this.state.instance} />
                            </OpenDialogButton>}>{instance.KubernetesClusterName}</DataTableRowWithActionColumn>) : (<DataTableRowColumn>{notSet()}</DataTableRowColumn>)}
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Storage Account</DataTableRowHeaderColumn>
                        {instance.StorageAccountName ? (<DataTableRowWithActionColumn action={
                            <OpenDialogButton label="Move To Another Storage Account">
                                <MoveFileStorageDialog instance={this.state.instance} />
                            </OpenDialogButton>}>{instance.StorageAccountName}</DataTableRowWithActionColumn>) : (<DataTableRowColumn>{notSet()}</DataTableRowColumn>)}
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Sql Server</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{instance.SqlServerName || notSet()}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Dns Prefix</DataTableRowHeaderColumn>
                        <DataTableRowWithActionColumn action={
                            <OpenDialogButton label="Change Dns Prefix" disabled={!this.canChangeDnsPrefix(instance.Status)}>
                                {this.changeDnsPrefixDialog()}
                            </OpenDialogButton>}>{instance.DnsPrefix}
                        </DataTableRowWithActionColumn>
                    </DataTableRow>
                    {reefResource && <DataTableRow>
                        <DataTableRowHeaderColumn>Domain</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{linkToInstance(instance, reefResource)}</DataTableRowColumn>
                    </DataTableRow>}
                </DataTableBody>
            </DataTable>
            <FormSectionHeading title="Maintenance"/>
            <DataTable>
                <DataTableBody>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Status</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{displayInstanceStatus(instance.Status)}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Better Uptime Status</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{displayBetterUptimeStatus(instance.BetterUptimeStatus)}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Secondary Uptime Status</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{displaySecondaryUptimeStatus(instance.SecondaryUptimeStatus)}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Should Be Monitored Status</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{instance.ShouldBeMonitored ? "Yes" : "No"}</DataTableRowColumn>
                    </DataTableRow>
                    {reefResource && <DataTableRow>
                        <DataTableRowHeaderColumn>Upgrade Strategy</DataTableRowHeaderColumn>
                        <DataTableRowWithActionColumn action={
                            <OpenDialogButton label="Change Upgrade Strategy">
                                <ChangeUpgradeStrategyDialog
                                    instance={instance}
                                    onSaved={(updatedInstance) => this.setState({instance: updatedInstance})}
                                />
                            </OpenDialogButton>}>
                            {instance.UpgradeStrategy && <>
                                {upgradeStrategyTypeDescriptions[instance.UpgradeStrategy.UpgradeStrategyType]}
                                {instance.UpgradeStrategy.UpgradeStrategyType === UpgradeStrategyType.TrackBranchUpgradeStrategy &&
                                  <>
                                      {" ("}
                                      <ExternalLink href={`https://github.com/OctopusDeploy/OctopusDeploy/tree/${(instance.UpgradeStrategy as TrackBranchUpgradeStrategy).BranchName}`}>
                                          {(instance.UpgradeStrategy as TrackBranchUpgradeStrategy).BranchName}
                                      </ExternalLink>
                                      {")"}
                                  </>}
                                {isSpecifiedVersionUpgradeStrategyType(instance.UpgradeStrategy.UpgradeStrategyType) &&
                                    ` (${(instance.UpgradeStrategy as SpecifiedVersionUpgradeStrategy).Version})`}
                            </>}
                        </DataTableRowWithActionColumn>
                    </DataTableRow>}
                    <CleanUpStrategyRows
                      instance={instance}
                      setInstance={(instance: HostedInstanceResource) => this.setState({instance})}
                      errors={this.state.errors}
                      busy={this.state.busy}
                      doBusyTask={this.doBusyTask}
                    />
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Current Version</DataTableRowHeaderColumn>
                        <DataTableRowWithActionColumn action={
                            <OpenDialogButton label="Correct Version">
                                <CorrectInstanceVersionDialog
                                    instance={instance}
                                    onSaved={(updatedInstance) => this.setState({instance: updatedInstance})}
                                />
                            </OpenDialogButton>}>
                            {instance.Version || "[Not Set]"}
                        </DataTableRowWithActionColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Allow Local Authentication</DataTableRowHeaderColumn>
                        <DataTableRowWithActionColumn action={
                            <OpenDialogButton label="Change Local Authentication">
                                {this.changeAllowLocalHostOctopusPortalSignInDialog()}
                            </OpenDialogButton>}>
                            {instance.AllowLocalHostOctopusPortalSignIn ? "Yes" : "No"}
                        </DataTableRowWithActionColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Next Outage Window</DataTableRowHeaderColumn>
                        <DataTableRowColumn>
                            <NextOutageWindowDisplay instance={instance}/>
                        </DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Outage Window</DataTableRowHeaderColumn>
                        <DataTableRowWithActionColumn action={
                            <OpenDialogButton label="Change Outage Window">
                                {this.changeOutageWindowDialog()}
                            </OpenDialogButton>}>
                            <OutageWindowDisplay instance={instance}/>
                        </DataTableRowWithActionColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Belongs To Pool</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{instance.IsInPool ? "Yes" : "No"}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Auth Providers</DataTableRowHeaderColumn>
                        <DataTableRowWithActionColumn action={
                            <OpenDialogButton label="Change Auth Providers">
                                {this.installLegacyCloudAuthProvidersDialog()}
                            </OpenDialogButton>}>
                            {instance.AuthProvidersToInstall.join(", ")}
                        </DataTableRowWithActionColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Recurring Job Schedule Offset</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{instance.RecurringJobScheduleOffset}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>OIDC Admin External ID</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{instance.OidcIdentityOptions?.ExternalId ?? "N/A"}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>OIDC Audience</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{instance.OidcIdentityOptions?.Audience ?? "N/A"}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Instance Database Users</DataTableRowHeaderColumn>
                        <DataTableRowColumn>
                            {[instance.SqlLoginUsername, instance.ReadOnlySqlLoginUsername].join(", ")}
                        </DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Master Key Length</DataTableRowHeaderColumn>
                        <DataTableRowColumn>
                            {instance.MasterKeyLengthInBits} bit
                        </DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Database Backup Retention</DataTableRowHeaderColumn>
                        <DataTableRowWithActionColumn action={
                            <OpenDialogButton label="Change Backup Retention">
                                {this.changeBackupRetentionDialog()}
                            </OpenDialogButton>}>
                            {ChangeBackupRetentionDialog.getBackupRetentionDisplay(instance.DatabaseBackupWeeklyLongTermRetention)}
                        </DataTableRowWithActionColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Customer IP Address Allow List</DataTableRowHeaderColumn>
                        <DataTableRowWithActionColumn action={
                            <NavigationButton label="View denied request logs" external href={externalSystemLinks.sumoLogic.instanceIpAddressAllowListDeniedRequests(instance)} type={NavigationButtonType.Secondary}/>
                        }>
                            {this.sortIpPrefixes(instance.CustomerIpAddressAllowList).join(", ")}
                        </DataTableRowWithActionColumn>
                    </DataTableRow>
                </DataTableBody>
            </DataTable>
            <FormSectionHeading title="Resource Allocation" />
            <DataTable>
                <DataTableBody>
                    <DataTableRow className={cn({[modularExecutionsVisibilityDimmed]: isModularExecutionsEnabled !== false})}>
                        <DataTableRowHeaderColumn>Legacy CPU</DataTableRowHeaderColumn>
                        <DataTableRowWithActionColumn action={
                            <OpenDialogButton label="Configure Legacy Compute Resources">
                                {this.configureLegacyComputeResources()}
                            </OpenDialogButton>}
                            title={cpuUsage ? "CPU usage last updated " + moment(cpuUsage.LastUpdated).fromNow() : ""}>
                            {cpuUsage ? `${cpuUsage.Usage}%` : "not available"} / Request {instance.LegacyCPURequest} - Limit {instance.LegacyCPULimit}
                        </DataTableRowWithActionColumn>
                    </DataTableRow>
                    <DataTableRow className={cn({[modularExecutionsVisibilityDimmed]: isModularExecutionsEnabled !== false})}>
                        <DataTableRowHeaderColumn>Legacy Memory (RSS)</DataTableRowHeaderColumn>
                        <DataTableRowColumn title={podMemoryUsage ? "Pod memory usage last updated " + moment(podMemoryUsage.LastUpdated).fromNow() : ""}>
                            {podMemoryUsage ? `${podMemoryUsage.Usage}%` : "not available"} / Request {instance.LegacyMemoryRequest} - Limit {instance.LegacyMemoryLimit}
                        </DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Modular Executions</DataTableRowHeaderColumn>
                        <DataTableRowColumn>
                            {isModularExecutionsEnabled === null ? "Unknown" : isModularExecutionsEnabled ? "Enabled" : "Disabled"}
                        </DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow className={cn({[modularExecutionsVisibilityDimmed]: isModularExecutionsEnabled !== true})}>
                        <DataTableRowHeaderColumn>Modelling CPU</DataTableRowHeaderColumn>
                        <DataTableRowWithActionColumn action={
                            <OpenDialogButton label="Configure Modular Executions Compute Resources">
                                {this.configureModularExecutionsComputeResources()}
                            </OpenDialogButton>}>
                            Request {instance.ModellingCPURequest} - Limit {instance.ModellingCPULimit}
                        </DataTableRowWithActionColumn>
                    </DataTableRow>
                    <DataTableRow className={cn({[modularExecutionsVisibilityDimmed]: isModularExecutionsEnabled !== true})}>
                        <DataTableRowHeaderColumn>Modelling Memory</DataTableRowHeaderColumn>
                        <DataTableRowColumn>
                            Request {instance.ModellingMemoryRequest} - Limit {instance.ModellingMemoryLimit}
                        </DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow className={cn({[modularExecutionsVisibilityDimmed]: isModularExecutionsEnabled !== true})}>
                        <DataTableRowHeaderColumn>Executions CPU</DataTableRowHeaderColumn>
                        <DataTableRowColumn>
                            Request {instance.ExecutionsCPURequest} - Limit {instance.ExecutionsCPULimit}
                        </DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow className={cn({[modularExecutionsVisibilityDimmed]: isModularExecutionsEnabled !== true})}>
                        <DataTableRowHeaderColumn>Executions Memory</DataTableRowHeaderColumn>
                        <DataTableRowColumn>
                            Request {instance.ExecutionsMemoryRequest} - Limit {instance.ExecutionsMemoryLimit}
                        </DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow className={cn({[modularExecutionsVisibilityDimmed]: isModularExecutionsEnabled !== true})}>
                        <DataTableRowHeaderColumn>Executions HPA</DataTableRowHeaderColumn>
                        <DataTableRowColumn>
                            {isExecutionsHPAEnabled === null ? "Unknown" : isExecutionsHPAEnabled ? "Enabled" : "Disabled"}
                            {isExecutionsHPAEnabled ? (isExecutionsHPAScaleDownEnabled ? " - Scale up and down" : " - Scale up only") : ""}
                        </DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow className={cn({[modularExecutionsVisibilityDimmed]: isModularExecutionsEnabled !== true})}>
                        <DataTableRowHeaderColumn>Executions Replicas</DataTableRowHeaderColumn>
                        <DataTableRowWithActionColumn action={
                            <OpenDialogButton label="Configure Executions Replicas">
                                {this.configureExecutionsReplicas()}
                            </OpenDialogButton>}>
                            {isExecutionsHPAEnabled === false || instance.ExecutionsMinimumReplicas === instance.ExecutionsMaximumReplicas
                                ? instance.ExecutionsMinimumReplicas
                                : `(min) ${instance.ExecutionsMinimumReplicas} <--> ${instance.ExecutionsMaximumReplicas} (max)`}
                        </DataTableRowWithActionColumn>
                    </DataTableRow>
                    <DataTableRow className={cn({[modularExecutionsVisibilityDimmed]: (isModularExecutionsEnabled !== true)})}>
                        <DataTableRowHeaderColumn>Executions HPA Target CPU Utilization</DataTableRowHeaderColumn>
                        <DataTableRowColumn>
                            {instance.ExecutionsHPATargetCPUAverageUtilization}%
                        </DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>File Storage</DataTableRowHeaderColumn>
                        <DataTableRowWithActionColumn action={
                            <OpenDialogButton label="Change storage quota">
                                <ChangeStorageQuotaDialog
                                    instance={instance}
                                    onSaved={(updatedInstance, updatedMetrics) => this.setState({instance: updatedInstance, instanceUsageMetrics: updatedMetrics})}
                                />
                            </OpenDialogButton>}
                            title={fileUsage ? "File usage last updated " + moment(fileUsage.LastUpdated).fromNow() : ""}>
                                {fileUsage ? `${bytesToGigaBytes(fileUsage.CurrentValue)} / ${bytesToGigaBytes(fileUsage.Limit)} GB` : `not available / ${gigaBytesToGigaBytes(instance.SharesQuotaInGb)} GB`}
                        </DataTableRowWithActionColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Executions File Storage</DataTableRowHeaderColumn>
                        <DataTableRowColumn title={executionsFileUsage ? "Executions file usage last updated " + moment(executionsFileUsage.LastUpdated).fromNow() : ""}>
                            {executionsFileUsage ? `${bytesToGigaBytes(executionsFileUsage.CurrentValue)} / ${bytesToGigaBytes(executionsFileUsage.Limit)} GB` : "not available"}
                        </DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Database Storage</DataTableRowHeaderColumn>
                        {databaseLimits ? <DataTableRowWithActionColumn action={
                            <OpenDialogButton label="Change database max size">
                                {this.changeDatabaseMaxSize()}
                            </OpenDialogButton>}
                            title={this.state.instanceUsageMetrics.DatabaseSize ? "Database size last updated " + moment(this.state.instanceUsageMetrics.DatabaseSize.LastUpdated).fromNow() : ""}>
                            {this.showDatabaseStorage()}
                        </DataTableRowWithActionColumn> : (<DataTableRowColumn>{this.showDatabaseStorage()}</DataTableRowColumn>)}
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Database CPU</DataTableRowHeaderColumn>
                        <DataTableRowWithActionColumn action={
                            <OpenDialogButton label="Change Database Service Level">
                                {this.changeDatabaseScale()}
                            </OpenDialogButton>}
                            title={databaseCpuUsage ? "Database CPU usage last updated " + moment(databaseCpuUsage.LastUpdated).fromNow() : ""}>
                            {instance.DatabaseServiceLevel && <>
                                {databaseCpuUsage ? `${databaseCpuUsage.Usage.toFixed(2)}%` : "not available"}
                                {` / ${this.getVCoreCount(instance.DatabaseServiceLevel)} vCore${this.getVCoreCount(instance.DatabaseServiceLevel) == 1 ? "" : "s"} (`}
                                {`${instance.DatabaseServiceLevel})`}
                            </>}
                        </DataTableRowWithActionColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Database Connection Max Pool Size</DataTableRowHeaderColumn>
                        <DataTableRowWithActionColumn action={
                            <OpenDialogButton label="Change max pool size">
                                {this.changeDatabaseConnectionMaxPoolSize()}
                            </OpenDialogButton>}>
                                {instance.DatabaseConnectionMaxPoolSize}
                        </DataTableRowWithActionColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Message Bus Max Concurrent Pumps</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{instance.OctopusMessageBusMaxConcurrentPumps}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Local Cache</DataTableRowHeaderColumn>
                        <DataTableRowColumn>
                            {`${instance.CacheDirectoryMaxSizeInGb} GB`}
                        </DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow className={cn({[modularExecutionsVisibilityDimmed]: isModularExecutionsEnabled === true})}>
                        <DataTableRowHeaderColumn>Legacy Replicas</DataTableRowHeaderColumn>
                        {
                            this.state.environment !== "Production"
                            ?
                                <DataTableRowWithActionColumn action={
                                    <OpenDialogButton label="Configure Instance Legacy Replicas">
                                        {this.configureInstanceLegacyReplicas()}
                                    </OpenDialogButton>}>
                                    {instance.LegacyReplicas.toString() + (instance.ZoneDiversityType == ZoneDiversityType.Forced ? " (Forced Zone Diversity)" : "")}
                                </DataTableRowWithActionColumn>
                            :
                                <DataTableRowColumn>
                                    {instance.LegacyReplicas.toString() + (instance.ZoneDiversityType == ZoneDiversityType.Forced ? " (Forced Zone Diversity)" : "")}
                                </DataTableRowColumn>
                        }
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Redis CPU</DataTableRowHeaderColumn>
                        <DataTableRowWithActionColumn action={
                            <OpenDialogButton label="Configure Redis Resources">
                                {this.configureRedisResources()}
                            </OpenDialogButton>}>
                            Request {instance.RedisCPURequest} - Limit {instance.RedisCPULimit}
                        </DataTableRowWithActionColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Redis Memory</DataTableRowHeaderColumn>
                        <DataTableRowColumn>
                            Request {instance.RedisMemoryRequest} - Limit {instance.RedisMemoryLimit}
                        </DataTableRowColumn>
                    </DataTableRow>
                </DataTableBody>
            </DataTable>
            <FormSectionHeading title="Dynamic Worker"/>
            <DataTable>
                <DataTableBody>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Default Worker Pool Type</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{instance.DefaultDynamicWorkerPoolType}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Worker Decommission After</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{instance.WorkerDecommissionAfter}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Worker Idle Time Before Deletion</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{instance.WorkerIdleTimeBeforeDeletion}</DataTableRowColumn>
                    </DataTableRow>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Worker Maximum Lease Duration</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{instance.WorkerMaximumLeaseDuration}</DataTableRowColumn>
                    </DataTableRow>
                </DataTableBody>
            </DataTable>
            {this.getOutboundPrivateLinkSection()}
            {this.getCloudSubscriptionSection()}
            <FormSectionHeading title="Instance Limits"/>
            {this.getInstanceLimitSection()}
            <InstanceEnvironmentVariableDetails
                busy={this.state.busy}
                errors={this.state.errors}
                redirectToInstanceTasks={this.redirectToInstanceTasks}
                instanceEnvironmentVariables={this.state.instanceEnvironmentVariables}
                instance={instance}/>
            <InstanceAssignedEnvironmentVariableDetails
                busy={this.state.busy}
                errors={this.state.errors}
                redirectToInstanceTasks={this.redirectToInstanceTasks}
                instanceAssignedEnvironmentVariables={this.state.instanceAssignedEnvironmentVariables}
                instance={instance}/>
        </div>;
    }

    private showDatabaseStorage() {
        const databaseUsage = this.state.instanceUsageMetrics && this.state.instanceUsageMetrics.DatabaseSize;
        const formattedUsage = databaseUsage
            ? `${bytesToGigaBytes(databaseUsage.CurrentValue)} / ${megaBytesToGigaBytes(this.state.instance.DatabaseMaxSizeInMb)} GB`
            : `not available / ${megaBytesToGigaBytes(this.state.instance.DatabaseMaxSizeInMb)} GB`

        const allocatedStorage = this.state.instanceUsageMetrics && this.state.instanceUsageMetrics.DatabaseAllocated;
        return allocatedStorage
            ? `${formattedUsage} (${bytesToGigaBytes(allocatedStorage.CurrentValue)} GB allocated)`
            : formattedUsage;
    }

    private async moveReef(request: Partial<HostedInstanceMoveReefRequest>) {
        await this.doBusyTask(async () => {
            const instance = await repository.HostedInstances.moveReef(this.state.instance, request as HostedInstanceMoveReefRequest);
            this.setState({instance});
        });
        await this.showNewTaskWindow();

        return true;
    }

    private configureDatabase(request: Partial<HostedInstanceConfigureDatabaseRequest>) {
        return this.doBusyTask(async () => {
            const instance = await repository.HostedInstances.configureDatabase(this.state.instance, request as HostedInstanceConfigureDatabaseRequest);
            const instanceUsageMetrics = await repository.HostedInstances.getUsage(instance.Id);
            this.setState({instance, instanceUsageMetrics});
            return true;
        });
    }

    private scaleDatabase(request: Partial<HostedInstanceDatabaseScaleRequest>) {
        return this.doBusyTask(async () => {
            const instance = await repository.HostedInstances.scaleDatabase(this.state.instance, request as HostedInstanceDatabaseScaleRequest);
            this.setState({instance});
            return true;
        });
    }

    private configureBackupRetention(request: Partial<HostedInstanceConfigureDatabaseBackupRetentionRequest>) {
        return this.doBusyTask(async () => {
            const instance = await repository.HostedInstances.configureBackupRetention(this.state.instance, request as HostedInstanceConfigureDatabaseBackupRetentionRequest);
            this.setState({instance});
            return true;
        });
    }

    private changeAllowLocalHostOctopusPortalSignIn(request: Partial<HostedInstanceAllowLocalHostOctopusPortalSignInRequest>) {
        return this.doBusyTask(async () => {
            const instance = await repository.HostedInstances.changeAllowLocalHostOctopusPortalSignIn({ instance: this.state.instance, request: (request as HostedInstanceAllowLocalHostOctopusPortalSignInRequest) });
            this.setState({instance});
            return true;
        });
    }

    private installLegacyCloudAuthProviders(request: Partial<HostedInstanceInstallLegacyCloudAuthProvidersRequest>) {
        return this.doBusyTask(async () => {
            const instance = await repository.HostedInstances.installLegacyCloudAuthProviders({ instance: this.state.instance, request: (request as HostedInstanceInstallLegacyCloudAuthProvidersRequest) });
            this.setState({instance});
            return true;
        });
    }

    private changeOutageWindow(request: Partial<HostedInstanceOutageWindowRequest>) {
        return this.doBusyTask(async () => {
            const instance = await repository.HostedInstances.changeOutageWindow(this.state.instance, request as HostedInstanceOutageWindowRequest);
            this.setState({instance});
            return true;
        });
    }

    private async showNewTaskWindow(taskId?: string) {
        if (taskId != null) {
            this.setState({recentlyCreatedTask: taskId});
            return;
        }

        const tasks = await repository.BackgroundTasks.listSummaries({relatedTo: [this.props.instanceId], take: 1});
        const latest = tasks.Resources[0];
        if (latest != null && latest.Finished == null) {
            this.setState({recentlyCreatedTask: latest.Id});
        }
    }

    private snapshotFileShare() {
        return this.doBusyTask(async () => {
            await repository.HostedInstances.snapshotFileShare(this.state.instance.Id);
            this.setState({redirectTo: routeLinks.instances.instance(this.state.instance.Id).tasks});
        });
    }

    private restoreDatabase(request: Partial<DatabaseRestoreRequest>) {
        return this.doBusyTask(async () => {
            await repository.HostedInstances.restoreDatabase(this.state.instance, request as DatabaseRestoreRequest);
            this.setState({redirectTo: routeLinks.instances.instance(this.state.instance.Id).tasks});
        });
    }

    private moveToAnotherReef() {
        const availableReefs = Array.from(this.state.reefs
            .values())
            .filter(r => r.Id !== this.state.instance.ReefId)
            .map(r => ({text: reefDisplayLabel(r), value: r.Id}));

        return <SaveDialogLayout title={"Move Between Reefs"}
                                 busy={this.state.busy}
                                 saveButtonDisabled={!this.state.moveReefRequest.DestinationReefId}
                                 errors={this.state.errors}
                                 onSaveClick={() => this.moveReef(this.state.moveReefRequest)}>
            <Callout type="warning" title="Warning">
                <p>Move operations can cause longer than expected down time for instances. Please make sure you understand the considerations and process before proceeding -
                    see <ExternalLink href="https://github.com/OctopusDeploy/hosted-docs/blob/main/how-to/move-a-cloud-instance-between-reefs.md">How to Move Instances</ExternalLink> for more details.</p>
            </Callout>
            <Select value={this.state.moveReefRequest.DestinationReefId}
                    onChange={DestinationReefId => this.updateMoveReefRequestReef(DestinationReefId)}
                    items={availableReefs}
                    fieldName="ReefId"
                    hintText="The Reef that the Instance should be moved to"/>
            <Note><b>Current Reef: </b>{reefDisplayLabel(this.getReefFor(this.state.instance))}</Note>
            <Checkbox value={this.state.moveReefRequest.IncludeResources}
                      onChange={IncludeResources => this.updateMoveReefRequest({IncludeResources})}
                      label={"Include Resources"}/>
            <div>Disable this option only if rolling back from a failed migration.</div>

            <BooleanRadioButtonGroup value={this.state.moveReefRequest.MoveDuringOutageWindow}
                              onChange={moveDuringOutageWindow => this.updateMoveReefRequest({MoveDuringOutageWindow: moveDuringOutageWindow})}>
                <BooleanRadioButton value={true} label="Move During Outage Window"/>
                <BooleanRadioButton value={false} label="Move Immediately"/>
            </BooleanRadioButtonGroup>
        </SaveDialogLayout>;
    }

    private changeOutageWindowDialog() {
        return <SaveDialogLayout title={"Change Outage Window"}
                                 busy={this.state.busy}
                                 errors={this.state.errors}
                                 onSaveClick={() => this.changeOutageWindow(this.state.changeOutageWindowRequest)}>

            <Callout type="warning" title="Outage Window">
                <p>
                    The outage window for this Instance is managed by Control Center and any changes to it should be made there.
                    Use this dialog only in case of emergency.
                </p>

                <p>
                    Any values specified here will be overwritten by Control Center in the future.
                </p>
            </Callout>

            <Select value={this.state.changeOutageWindowRequest.OutageWindowFrequency}
                    onChange={OutageWindowFrequency => this.updateOutageWindow({OutageWindowFrequency: OutageWindowFrequency as OutageWindowFrequency})}
                    items={Object.entries(outageWindowFrequencyDescriptions).map(([value, text]) => ({value, text}))}
                    fieldName="Frequency"/>

            <Select value={this.state.changeOutageWindowRequest.OutageWindowTimeZone}
                    onChange={OutageWindowTimeZone => this.updateOutageWindow({OutageWindowTimeZone})}
                    items={timeZoneSelectItems}
                    fieldName="Time Zone"/>

            <label>Start</label>
            <TimePicker value={LocalTime.toDate(this.state.changeOutageWindowRequest.OutageWindowStart)}
                        onChange={(value) => this.updateOutageWindow({OutageWindowStart: LocalTime.fromDate(value)})}/>

            <label>End</label>
            <TimePicker value={LocalTime.toDate(this.state.changeOutageWindowRequest.OutageWindowEnd)}
                        onChange={(value) => this.updateOutageWindow({OutageWindowEnd: LocalTime.fromDate(value)})}/>
        </SaveDialogLayout>;
    }

    private changeAllowLocalHostOctopusPortalSignInDialog() {
        return <SaveDialogLayout title={"Allow Localhost Authentication?"}
                                 busy={this.state.busy}
                                 errors={this.state.errors}
                                 onSaveClick={() => this.changeAllowLocalHostOctopusPortalSignIn(this.state.changeAllowLocalHostOctopusPortalSignInRequest)}>

            <Callout type="warning" title="Local Octopus Portal Development">
                Normally, Octopus ID will reject any request that tries to redirect to anywhere but the Octopus Cloud Instance.
                Enabling this option will allow local Octopus Portal development against this Cloud Instance.
            </Callout>

            <BooleanRadioButtonGroup value={this.state.changeAllowLocalHostOctopusPortalSignInRequest.AllowLocalHostOctopusPortalSignIn}
                              onChange={allowLocalhostAuthentication => this.updateAllowLocalhostAuthenticationRequest({AllowLocalHostOctopusPortalSignIn: allowLocalhostAuthentication})}>
                <BooleanRadioButton value={true} label="Allow localhost sign-on using OctopusID"/>
                <BooleanRadioButton value={false} label="Do not allow localhost sign-on"/>
            </BooleanRadioButtonGroup>
        </SaveDialogLayout>;
    }

    private installLegacyCloudAuthProvidersDialog() {
        return <SaveDialogLayout title={"Install Legacy Cloud Auth Providers?"}
                                 busy={this.state.busy}
                                 errors={this.state.errors}
                                 onSaveClick={() => this.installLegacyCloudAuthProviders(this.state.installLegacyCloudAuthProvidersRequest)}>

            <Callout type="warning" title="Legacy Cloud Auth Providers">
                Installing legacy cloud auth providers will install the following auth providers: UsernamePassword, AzureAD, GoogleApps, Okta, Guest, and Octopus ID.
            </Callout>

            <Checkbox label="Reprovision During Maintenance Window"
                      value={this.state.installLegacyCloudAuthProvidersRequest.ReprovisionDuringMaintenanceWindow}
                      onChange={ReprovisionDuringMaintenanceWindow => this.updateInstallLegacyCloudAuthProviders({ReprovisionDuringMaintenanceWindow})}
            />

            <BooleanRadioButtonGroup value={this.state.installLegacyCloudAuthProvidersRequest.InstallLegacyCloudAuthProviders}
                              onChange={installLegacyCloudAuthProviders => this.updateInstallLegacyCloudAuthProviders({InstallLegacyCloudAuthProviders: installLegacyCloudAuthProviders})}>
                <BooleanRadioButton value={true} label="Enable Legacy Cloud Auth Providers"/>
                <BooleanRadioButton value={false} label="Disable Legacy Cloud Auth Providers"/>
            </BooleanRadioButtonGroup>
        </SaveDialogLayout>;
    }

    private changeDatabaseMaxSize() {
        return <SaveDialogLayout title={"Change database max size in GB"}
                                 errors={this.state.errors}
                                 busy={this.state.busy}
                                 onSaveClick={() => this.configureDatabase(this.state.configureDatabaseRequest)}>

            <AreYouSureYouWantToIncreaseTheLimitCallout />

            <Callout type="warning" title="Size">
                The maximum database size for this Instance is currently limited by the {this.state.databaseLimits.SizeLimitSource} to {this.state.databaseLimits.MaxSizeInMb / 1024} GB.
                Larger sizes may be available by changing the database service tier, see <ExternalLink href="https://app.shortcut.com/octopusdeploy/story/20314/scaling-db-storage-size-should-scale-servicelevelobjective-if-required#activity-21316">this discussion</ExternalLink>.
            </Callout>

            <ManualScalingCallout />


            <Select value={this.state.configureDatabaseRequest.MaxSizeInMb.toString()}
                    onChange={maxSize => this.updateConfigureDatabaseRequest({MaxSizeInMb: Number(maxSize)})}
                    items={Array.from(this.state.databaseLimits.AvailableDatabaseMaxSizesInMb, this.dbSize)}
                    fieldName="MaxSizeInMb"
                    hintText="Max database size for this Instance"
            />

            <BooleanRadioButtonGroup value={this.state.configureDatabaseRequest.RunDuringOutageWindow}
                              onChange={scaleDuringOutageWindow => this.updateConfigureDatabaseRequest({RunDuringOutageWindow: scaleDuringOutageWindow})}>
                <BooleanRadioButton value={true} label="Scale During Outage Window" isDefault={true} />
                <BooleanRadioButton value={false} label="Scale Immediately"/>
            </BooleanRadioButtonGroup>


        </SaveDialogLayout>;
    }

    private changeDatabaseScale() {
        return <SaveDialogLayout title={"Change Database Scale"}
                                 errors={this.state.errors}
                                 busy={this.state.busy}
                                 onSaveClick={() => this.scaleDatabase(this.state.databaseScaleRequest)}
                                 saveButtonDisabled={this.isBusinessCriticalDatabaseServiceLevel(this.state.databaseScaleRequest.ServiceLevelObjective) && !this.state.canScaleToBusinessCritical}>
            {this.areYouSureYouWantToIncreaseTheDatabaseLevel()}
            <ManualScalingCallout />

            <Select value={this.state.databaseScaleRequest.ServiceLevelObjective}
                    onChange={serviceLevel => {
                        this.updateDatabaseScaleRequest({ServiceLevelObjective: serviceLevel});
                    }}
                    items={[
                        this.scaleTargetVCoreItem("GP_ElasticPool"),
                        this.scaleTargetVCoreItem("GP_Gen5_2"),
                        this.scaleTargetVCoreItem("GP_Gen5_4"),
                        this.scaleTargetVCoreItem("GP_Gen5_8"),
                        this.scaleTargetVCoreItem("GP_Gen5_16"),
                        this.scaleTargetVCoreItem("GP_Gen5_32"),
                        this.scaleTargetVCoreItem("GP_Gen5_40"),
                        this.scaleTargetVCoreItem("GP_Gen5_80"),
                        this.scaleTargetVCoreItem("GP_Gen5_128"),
                        this.scaleTargetVCoreItem("BC_Gen5_2"),
                        this.scaleTargetVCoreItem("BC_Gen5_4"),
                        this.scaleTargetVCoreItem("BC_Gen5_8"),
                        this.scaleTargetVCoreItem("BC_Gen5_14"),
                        this.scaleTargetVCoreItem("BC_Gen5_24"),
                        this.scaleTargetVCoreItem("BC_Gen5_32"),
                        this.scaleTargetVCoreItem("BC_Gen5_40"),
                        this.scaleTargetVCoreItem("BC_Gen5_80"),
                        this.scaleTargetVCoreItem("BC_Gen5_128")
                    ]}
                    fieldName="ScaleDirection"
                    hintText="Service level objective to scale the database to"
            />

            {this.isBusinessCriticalDatabaseServiceLevel(this.state.databaseScaleRequest.ServiceLevelObjective) &&
            <AreYouReallySureConfirmation title={"Are you sure this database should be on the Business Critical tier?"}
                                          warning={<>Instances moving to any Business Critical database service level must be done in collaboration with <a rel="noopener" target="_blank" href="https://octopusdeploy.slack.com/archives/C01Q95KPRM4">#cloud-platform-requests</a>.</>}
                                          requiredConfirmation={"business critical"}
                                          onChange={acknowledged => this.setState({canScaleToBusinessCritical: acknowledged})} />}

            <BooleanRadioButtonGroup value={this.state.databaseScaleRequest.RunDuringOutageWindow}
                              onChange={scaleDuringOutageWindow => this.updateDatabaseScaleRequest({RunDuringOutageWindow: scaleDuringOutageWindow})}>
                <BooleanRadioButton value={true} label="Scale During Outage Window" isDefault={true} />
                <BooleanRadioButton value={false} label="Scale Immediately"/>
            </BooleanRadioButtonGroup>
        </SaveDialogLayout>;
    }

    private changeBackupRetentionDialog() {
        return <ChangeBackupRetentionDialog 
            errors={this.state.errors} 
            busy={this.state.busy} 
            instance={this.state.instance} 
            onSaveClick={this.changeBackupRetentionRequestSent}/>
    }

    private dbSize(sizeInMb: number){
        const text = sizeInMb < 1024 ? `${sizeInMb} MB` : `${sizeInMb / 1024} GB`;
        return {text, value: sizeInMb.toString()};
    }

    private scaleTargetVCoreItem(serviceLevelObjective: string) {
        const vCores = this.getVCoreCount(serviceLevelObjective)
        const text = `${serviceLevelObjective} (${this.getVCoreDescription(serviceLevelObjective)}, ${this.getVCoreCount(serviceLevelObjective)} vCore${vCores == 1 ? "" : "s"})`;
        return {text, value: serviceLevelObjective};
    }

    private getVCoreDescription(serviceLevel: string) {
        const dtus = new Map<string, string>([
            ["GP_ElasticPool", "General Purpose"],
            ["GP_Gen5_2", "General Purpose"],
            ["GP_Gen5_4", "General Purpose"],
            ["GP_Gen5_8", "General Purpose"],
            ["GP_Gen5_16", "General Purpose"],
            ["GP_Gen5_32", "General Purpose"],
            ["GP_Gen5_40", "General Purpose"],
            ["GP_Gen5_80", "General Purpose"],
            ["GP_Gen5_128", "General Purpose"],
            ["BC_Gen5_2", "Business Critical"],
            ["BC_Gen5_4", "Business Critical"],
            ["BC_Gen5_8", "Business Critical"],
            ["BC_Gen5_14", "Business Critical"],
            ["BC_Gen5_24", "Business Critical"],
            ["BC_Gen5_32", "Business Critical"],
            ["BC_Gen5_40", "Business Critical"],
            ["BC_Gen5_80", "Business Critical"],
            ["BC_Gen5_128", "Business Critical"]]);
        return dtus.get(serviceLevel);
    }

    private getVCoreCount(serviceLevel: string) {
        const vCores = new Map<string, number>([
            ["GP_ElasticPool", 1],
            ["GP_Gen5_2", 2],
            ["GP_Gen5_4", 4],
            ["GP_Gen5_8", 8],
            ["GP_Gen5_16", 16],
            ["GP_Gen5_32", 32],
            ["GP_Gen5_40", 40],
            ["GP_Gen5_80", 80],
            ["GP_Gen5_128", 128],
            ["BC_Gen5_2", 2],
            ["BC_Gen5_4", 4],
            ["BC_Gen5_8", 8],
            ["BC_Gen5_14", 14],
            ["BC_Gen5_24", 24],
            ["BC_Gen5_32", 32],
            ["BC_Gen5_40", 40],
            ["BC_Gen5_80", 80],
            ["BC_Gen5_128", 128]]);
        return vCores.get(serviceLevel);
    }

    private isBusinessCriticalDatabaseServiceLevel(databaseServiceLevel: string) {
        return [
            "BC_Gen5_2",
            "BC_Gen5_4",
            "BC_Gen5_8",
            "BC_Gen5_14",
            "BC_Gen5_24",
            "BC_Gen5_32",
            "BC_Gen5_40",
            "BC_Gen5_80",
            "BC_Gen5_128",
        ].includes(databaseServiceLevel);
    }

    private changeDatabaseConnectionMaxPoolSize() {
        return <EditDatabaseMaxPoolSizeDialog instance={this.state.instance}
                                                     busy={this.state.busy}
                                                     errors={this.state.errors}
                                                     onSaveClick={this.redirectToInstanceTasks}/>;
    }

    private configureInstanceLegacyReplicas() {
        return <ConfigureInstanceLegacyReplicasDialog instance={this.state.instance}
                                                      busy={this.state.busy}
                                                      errors={this.state.errors}
                                                      onSaveClick={this.redirectToInstanceTasks}/>;
    }

    private configureLegacyComputeResources() {
        return <ConfigureLegacyComputeResourcesDialog instance={this.state.instance}
                                                      isModularExecutionsEnabled={this.state.modularExecutionsFeatures.IsEnabled}
                                                       busy={this.state.busy}
                                                       errors={this.state.errors}
                                                       onSaveClick={this.redirectToInstanceTasks}/>;
    }

    private configureModularExecutionsComputeResources() {
        return <ConfigureModularExecutionsComputeResourcesDialog instance={this.state.instance}
                                                                 isModularExecutionsEnabled={this.state.modularExecutionsFeatures.IsEnabled}
                                                                  busy={this.state.busy}
                                                                  errors={this.state.errors}
                                                                  onSaveClick={this.redirectToInstanceTasks}/>;
    }

    private configureExecutionsReplicas() {
        return <ConfigureInstanceExecutionsReplicasDialog instance={this.state.instance}
                                                          isModularExecutionsEnabled={this.state.modularExecutionsFeatures.IsEnabled}
                                                          isExecutionsHPAEnabled={this.state.modularExecutionsFeatures.IsExecutionsHPAEnabled}
                                                          isRunningInProduction={this.state.environment === EnvironmentName.Production}
                                                          busy={this.state.busy}
                                                          errors={this.state.errors}
                                                          onSaveClick={this.redirectToInstanceTasks}/>;
    }

    private configureRedisResources() {
        return <ConfigureRedisResourcesDialog instance={this.state.instance}
                                              busy={this.state.busy}
                                              errors={this.state.errors}
                                              onSaveClick={this.redirectToInstanceTasks}/>;
    }

    private snapshotFileShareDialog() {
        return <SaveDialogLayout title={"Snapshot File Share"}
                                 busy={this.state.busy}
                                 errors={this.state.errors}
                                 onSaveClick={() => this.snapshotFileShare()}>
            <label>Snapshot file share for Hosted Instance {this.state.instance.DnsPrefix}?</label>
        </SaveDialogLayout>;
    }

    private restoreDatabaseDialog() {
        return <SaveDialogLayout title={"Restore Database"}
                                 busy={this.state.busy}
                                 errors={this.state.errors}
                                 saveButtonLabel="Restore"
                                 onSaveClick={() => this.restoreDatabase(this.state.restoreDatabaseRequest)}>
            <label>Restore database for Hosted Instance {this.state.instance.DnsPrefix} (using UTC)?</label>
            <input type="date" onChange={this.handleDatabaseRestoreDateInputChange}/>
            <input type="time" onChange={this.handleDatabaseRestoreTimeInputChange}/>
        </SaveDialogLayout>;
    }

    private takeProcessDumpDialog() {
        return <TakeProcessDumpDialog instanceId={this.state.instance.Id}
                                      busy={this.state.busy}
                                      errors={this.state.errors}
                                      onSaveClick={this.redirectToBackgroundTask}/>;
    }

    private collectDotnetTraceDialog() {
        return <CollectDotnetTraceDialog instanceId={this.state.instance.Id}
                                      busy={this.state.busy}
                                      errors={this.state.errors}
                                      onSaveClick={this.redirectToBackgroundTask}/>;
    }

    private outboundPrivateLinkDialog() {
        return <ProvisionOutboundPrivateLinkDialog
            instanceId={this.state.instance.Id}
            initialAlias={this.state.instance.OutboundPrivateLinkServiceAlias}
            onTaskStarted={taskId => this.showNewTaskWindow(taskId)} />;
    }

    private deleteOutboundPrivateLinkDialog() {
        return <DeleteOutboundPrivateLinkDialog
            instanceId={this.state.instance.Id}
            onTaskStarted={taskId => this.showNewTaskWindow(taskId)} />;
    }

    private addOutboundPrivateLinkDnsRecordDialog() {
        return <AddOutboundPrivateLinkDnsRecordDialog
            instanceId={this.state.instance.Id}
            onTaskStarted={taskId => this.showNewTaskWindow(taskId)} />;
    }

    private deleteOutboundPrivateLinkDnsRecordDialog() {
        return <DeleteOutboundPrivateLinkDnsRecordDialog
            instanceId={this.state.instance.Id}
            onTaskStarted={taskId => this.showNewTaskWindow(taskId)} />;
    }

    private addUserAsAdminDialog() {
        return <SaveDialogLayout title={"Add User As Admin"}
                                 busy={this.state.busy}
                                 errors={this.state.errors}
                                 saveButtonLabel={"Yes"}
                                 cancelButtonLabel={"No"}
                                 onSaveClick={() => this.addUserAsAdmin()}>
            <p>Add yourself as a branch instance administator? </p>
            <Callout type="information" title={"Info"}>
                Please, make sure your user exists in the instance. Your user will be created when you login to the instance for the first time.
            </Callout>
        </SaveDialogLayout>;
    }

    private azureAuthDialog() {
        return <SaveDialogLayout title={"Add Azure Active Directory Login"}
                                 busy={this.state.busy}
                                 errors={this.state.errors}
                                 saveButtonLabel={"Yes"}
                                 cancelButtonLabel={"No"}
                                 onSaveClick={() => this.azureAuth()}>
            <p>Are you sure you want to add Azure AD login to <strong>{this.state.instance.DnsPrefix}</strong>?</p>
            <p>This will register the <ExternalLink href={"https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationMenuBlade/Authentication/appId/e1a76654-7b5d-4c01-a169-ab670cbc70c1/isMSAApp/"}>Test Instance</ExternalLink> Active Directory App on this Instance.
                Which will allow anyone in the Octopus Deploy <ExternalLink href={"https://portal.azure.com/#view/Microsoft_AAD_IAM/GroupDetailsMenuBlade/~/Overview/groupId/e0a4a590-800a-4b36-b0ae-ccc263575df2"}>Department: Engineering</ExternalLink> group to login with Admin rights.</p>
        </SaveDialogLayout>;
    }

    private reprovisioningDialog() {
        return <ReprovisionDialog instance={this.state.instance}
                                  instanceLimit={this.state.instanceLimit}
                                  reef={this.getReefFor(this.state.instance)}
                                  busy={this.state.busy}
                                  errors={this.state.errors}
                                  onSaveClick={this.reprovisioningRequestSent}/>;
    }

    private reconfigureResourcesDialog() {
        return <SaveDialogLayout title={"Reconfigure resources"}
                                 busy={this.state.busy}
                                 errors={this.state.errors}
                                 onSaveClick={() => this.reconfigureDeactivatedInstanceResources()}>
            <label>Reconfigure deactivated instance resources for {this.state.instance.DnsPrefix}?</label>
        </SaveDialogLayout>;
    }

    private upgradeDialog() {
        return <UpgradeDialog instance={this.state.instance}
                              reef={this.getReefFor(this.state.instance)}
                              busy={this.state.busy}
                              errors={this.state.errors}
                              onSaveClick={this.upgradeRequestSent}/>;
    }

    private downgradeDialog() {
        return <DowngradeDialog instance={this.state.instance}
                              reef={this.getReefFor(this.state.instance)}
                              busy={this.state.busy}
                              errors={this.state.errors}
                              onSaveClick={this.downgradeRequestSent}/>;
    }

    private changeDnsPrefixDialog() {
        return <ChangeDnsPrefixDialog instance={this.state.instance}
                              instanceLimit={this.state.instanceLimit}
                              busy={this.state.busy}
                              errors={this.state.errors}
                              onSaveClick={this.changeDnsPrefixRequestSent}/>;
    }

    private reactivationDialog() {
        const isLimitedLifetime = this.state.instance.CleanUpStrategy.CleanUpStrategyType === CleanUpStrategyType.LimitedLifetimeCleanUpStrategy;
        const reactivatePrompt = !isLimitedLifetime
            ? <p>Are you sure you want to reactivate <strong>{this.state.instance.DnsPrefix}</strong> Instance?</p>
            : <p>Are you sure you want to reactivate <strong>{this.state.instance.DnsPrefix}</strong> Instance for {(this.state.instance.CleanUpStrategy as LimitedLifetimeCleanUpStrategy).DeactivationInDays} days?</p>;

        return <SaveDialogLayout title={"Reactivate"}
                                 busy={this.state.busy}
                                 errors={this.state.errors}
                                 saveButtonLabel={"Yes"}
                                 cancelButtonLabel={"No"}
                                 onSaveClick={() => isLimitedLifetime ? this.extend() : this.reactivate()}>
            <WarnToReactivateViaBillingAlert action="reactivate" instanceLimit={this.state.instanceLimit} />
            {reactivatePrompt}
        </SaveDialogLayout>;
    }

    private runDatabaseMaintenanceDialog() {
        return <RunDatabaseMaintenanceDialog instanceId={this.state.instance.Id}
                                             version={this.state.instance.Version}
                                             busy={this.state.busy}
                                             errors={this.state.errors}
                                             onSaveClick={this.runDatabaseMaintenanceRequestSent}/>;
    }

    private revokeUsersSessionsDialog() {
        return <SaveDialogLayout title={"Revoke users sessions"}
            busy={this.state.busy}
            errors={this.state.errors}
            saveButtonLabel={"Yes"}
            cancelButtonLabel={"No"}
            onSaveClick={() => this.revokeUsersSessions()}>
            <p>Are you sure you want to revoke all users sessions for <strong>{this.state.instance.DnsPrefix}</strong> Instance?</p>
        </SaveDialogLayout>;
    }

    private cancelDatabaseMaintenanceDialog() {
        return <SaveDialogLayout title={"Cancel all Database Maintenance Tasks"}
            busy={this.state.busy}
            errors={this.state.errors}
            saveButtonLabel={"Yes"}
            cancelButtonLabel={"No"}
            onSaveClick={() => this.cancelDatabaseMaintenanceTasks()}>
            <p>Are you sure you want to cancel all running database maintenance tasks for <strong>{this.state.instance.DnsPrefix}</strong> Instance?</p>
        </SaveDialogLayout>;
    }

    private runSqlDialog() {
        return <RunSqlDialog instance={this.state.instance}
                             busy={this.state.busy}
                             errors={this.state.errors}
                             onSaveClick={this.redirectToInstanceTasks}/>;

    }

    private retrieveSqlDeadlockGraphs() {
        return <RetrieveSqlDeadlockGraphsDialog instance={this.state.instance}
                                                busy={this.state.busy}
                                                errors={this.state.errors}
                                                onSaveClick={this.redirectToInstanceTasks} />
    }

    private getLockWarning(instanceLock: InstanceTaskLockResource) {
        return <Callout type="warning" title={"Warning"}>
            <InternalLink to={routeLinks.instanceTaskLocks.filtered({ relatedToHostedInstanceIds: [instanceLock.HostedInstanceId]})}>Instance Lock</InternalLink> is currently held by a {displayTaskStatus(instanceLock.LockOwnerStatus)}
            <InternalLink to={routeLinks.backgroundTasks.task(instanceLock.LockOwnerId).root}> {instanceLock.LockOwnerId}</InternalLink>
        </Callout>;
    }

    private restoreFileShareDialog() {
        return <RestoreFileShareDialog instance={this.state.instance}
                                       busy={this.state.busy}
                                       errors={this.state.errors}/>;
    }

    private getReefFor(instance: HostedInstanceResource) {
        return this.state.reefs.get(instance.ReefId);
    }

    private updateOutageWindow(update: Partial<HostedInstanceOutageWindowRequest>) {
        return this.setState(prevState => ({changeOutageWindowRequest: {...prevState.changeOutageWindowRequest, ...update}}));
    }

    private updateMoveReefRequestReef(reefId: string) {
        return this.doBusyTask(async () => {
            this.updateMoveReefRequest({DestinationReefId: reefId});
        });
    }

    private updateMoveReefRequest(update: Partial<HostedInstanceMoveReefRequest>) {
        return this.setState(prevState => ({moveReefRequest: {...prevState.moveReefRequest, ...update}}));
    }

    private updateAllowLocalhostAuthenticationRequest(update: Partial<HostedInstanceAllowLocalHostOctopusPortalSignInRequest>) {
        return this.setState(prevState => ({changeAllowLocalHostOctopusPortalSignInRequest: {...prevState.changeAllowLocalHostOctopusPortalSignInRequest, ...update}}));
    }

    private updateInstallLegacyCloudAuthProviders(update: Partial<HostedInstanceInstallLegacyCloudAuthProvidersRequest>) {
        return this.setState(prevState => ({installLegacyCloudAuthProvidersRequest: {...prevState.installLegacyCloudAuthProvidersRequest, ...update}}));
    }

    private updateConfigureDatabaseRequest(update: Partial<HostedInstanceConfigureDatabaseRequest>) {
        return this.setState(prevState => ({configureDatabaseRequest: {...prevState.configureDatabaseRequest, ...update}}));
    }

    private updateDatabaseScaleRequest(update: Partial<HostedInstanceDatabaseScaleRequest>) {
        return this.setState(prevState => ({databaseScaleRequest: {...prevState.databaseScaleRequest, ...update}}));
    }

    private updateDatabaseBackupRetentionRequest(update: Partial<HostedInstanceConfigureDatabaseBackupRetentionRequest>) {
        return this.setState(prevState => ({databaseBackupRetentionRequest: {...prevState.databaseBackupRetentionRequest, ...update}}));
    }

    private getOutboundPrivateLinkSection() {
        const reef = this.getReefFor(this.state.instance);
        if (!reef || !reef.IsDedicated) {
            return null;
        }

        const isConfigured = !!this.state.instance.OutboundPrivateLinkServiceAlias;

        return <>
            <FormSectionHeading title="Outbound Private Link" />
            <DataTable>
                <DataTableBody>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Outbound Private Link Configured</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{convertBooleanToText(isConfigured)}</DataTableRowColumn>
                    </DataTableRow>
                    {isConfigured &&
                        <DataTableRow>
                            <DataTableRowHeaderColumn>Private Link Service Alias</DataTableRowHeaderColumn>
                            <DataTableRowColumn>{this.state.instance.OutboundPrivateLinkServiceAlias}</DataTableRowColumn>
                        </DataTableRow>
                    }
                    {this.state.instance.OutboundPrivateLinkDnsEntries?.length > 0 &&
                        <DataTableRow>
                            <DataTableRowHeaderColumn>Private DNS Zone FQDNs</DataTableRowHeaderColumn>
                            <DataTableRowColumn>
                                {this.state.instance.OutboundPrivateLinkDnsEntries.join(", ")}
                            </DataTableRowColumn>
                        </DataTableRow>
                    }
                </DataTableBody>
            </DataTable>
        </>;
    }

    private getCloudSubscriptionSection() {
        const limit = this.state.instanceLimit;

        if (!limit.CloudSubscriptionId) {
            return <>
                <FormSectionHeading title="Cloud Subscription"/>
                <UnstructuredFormSection>{"No cloud subscription associated with this Instance"}</UnstructuredFormSection>
            </>;
        }

        return <><FormSectionHeadingWithAction
            title="Cloud Subscription"
            action={<ActionList actions={ this.getCloudSubscriptionActions(limit)} /> }/>
            <DataTable>
                <DataTableBody>
                    <DataTableRow>
                        <DataTableRowHeaderColumn>Serial</DataTableRowHeaderColumn>
                        <DataTableRowColumn>{limit.CloudSubscriptionSerial}</DataTableRowColumn>
                    </DataTableRow>
                </DataTableBody>
            </DataTable>
        </>;
    }

    private renderScaleUpSignal(taskLimitUsage?: TaskLimitUsageResource) {
        const scaleUpSignal = taskLimitUsage && taskLimitUsage.ScaleUpSignal;
        // Absent for an instance that was idle throughout, as well as one not yet measured - there's nothing
        // to report either way.
        if (!scaleUpSignal) return <span>Not measured</span>;

        return <span>{`At cap ${scaleUpSignal.Value.toFixed(1)}% of the last 3 days`}</span>;
    }

    private renderScaleDownSignal(taskLimitUsage?: TaskLimitUsageResource) {
        const scaleDownSignal = taskLimitUsage && taskLimitUsage.ScaleDownSignal;
        if (!scaleDownSignal) return <span>Not measured</span>;

        return <span>{`${scaleDownSignal.Value.toFixed(1)}% saturated one tier down`}</span>;
    }

    private getInstanceLimitSection() {
        const instanceLimit = this.state.instanceLimit;
        const taskLimitUsage = this.state.instanceUsageMetrics && this.state.instanceUsageMetrics.TaskLimitUsage;

        return <DataTable>
            <DataTableBody>
                <InstanceDetailLicenseXml instance={this.state.instance} instanceLimit={instanceLimit} setInstanceLimit={instanceLimit => this.setState({ instanceLimit })} errors={this.state.errors} busy={this.state.busy} doBusyTask={this.doBusyTask} />
                <DataTableRow>
                    <DataTableRowHeaderColumn>Task Cap</DataTableRowHeaderColumn>
                    <DataTableRowColumn>{instanceLimit.TaskCap}</DataTableRowColumn>
                </DataTableRow>
                <DataTableRow>
                    <DataTableRowHeaderColumn>Hosting Task Limit</DataTableRowHeaderColumn>
                    {instanceLimit.TaskCap === "Unlimited"
                        ? <DataTableRowWithActionColumn action={
                            <OpenDialogButton label="Change hosting task limit">
                                <ConfigureHostingTaskLimitDialog instance={this.state.instance}
                                                                 instanceLimit={instanceLimit}
                                                                 busy={this.state.busy}
                                                                 errors={this.state.errors}
                                                                 onSaveClick={this.redirectToInstanceTasks}/>
                            </OpenDialogButton>}>
                            {instanceLimit.HostingTaskLimit}
                        </DataTableRowWithActionColumn>
                        : <DataTableRowColumn>{instanceLimit.HostingTaskLimit}</DataTableRowColumn>}
                </DataTableRow>
                <DataTableRow>
                    <DataTableRowHeaderColumn>Task Limit Usage</DataTableRowHeaderColumn>
                    <DataTableRowColumn
                        title={taskLimitUsage && taskLimitUsage.ScaleUpSignal ? "Last updated " + moment(taskLimitUsage.ScaleUpSignal.LastUpdated).fromNow() : ""}>
                        {this.renderScaleUpSignal(taskLimitUsage)}
                    </DataTableRowColumn>
                </DataTableRow>
                <DataTableRow>
                    <DataTableRowHeaderColumn>Task Limit Scale-Down Signal</DataTableRowHeaderColumn>
                    <DataTableRowColumn
                        title={taskLimitUsage && taskLimitUsage.ScaleDownSignal ? "Last updated " + moment(taskLimitUsage.ScaleDownSignal.LastUpdated).fromNow() : ""}>
                        {this.renderScaleDownSignal(taskLimitUsage)}
                    </DataTableRowColumn>
                </DataTableRow>
                <DataTableRow>
                    <DataTableRowHeaderColumn>Storage Limit (GB)</DataTableRowHeaderColumn>
                    <DataTableRowColumn>{instanceLimit.StorageLimitInGb}</DataTableRowColumn>
                </DataTableRow>
                <DataTableRow>
                    <DataTableRowHeaderColumn>Default Retention (max. 30 days)</DataTableRowHeaderColumn>
                    <DataTableRowColumn>{instanceLimit.DefaultRetentionInDays}</DataTableRowColumn>
                </DataTableRow>
                <DataTableRow>
                    <DataTableRowHeaderColumn>Use baseline resources</DataTableRowHeaderColumn>
                    <DataTableRowColumn>{convertBooleanToText(instanceLimit.UseBaselineResources)}</DataTableRowColumn>
                </DataTableRow>
                <DataTableRow>
                    <DataTableRowHeaderColumn>Connection Agent Limit</DataTableRowHeaderColumn>
                    <DataTableRowColumn>{instanceLimit.ConnectionAgentLimit}</DataTableRowColumn>
                </DataTableRow>
            </DataTableBody>
        </DataTable>;
    }

    private handleDeleteConfirm = async () => {
        return this.doBusyTask(async () => {
            await repository.HostedInstances.delete(this.state.instance);
            await this.redirectToInstanceTasks();
        });
    };

    private reprovisioningRequestSent = async () => {
        return this.doBusyTask(async () => {
            await this.redirectToInstanceTasks();
        });
    };

    private upgradeRequestSent = async () => {
        return this.doBusyTask(async () => {
            await this.redirectToInstanceTasks();
        });
    };

    private downgradeRequestSent = async () => {
        return this.doBusyTask(async () => {
            await this.redirectToInstanceTasks();
        });
    };

    private runDatabaseMaintenanceRequestSent = async () => {
        return this.doBusyTask(async () => {
            await this.redirectToInstanceTasks();
        });
    };

    private changeDnsPrefixRequestSent  = async () => {
        return this.doBusyTask(async () => {
            await this.redirectToInstanceTasks();
        });
    };

    private changeBackupRetentionRequestSent = (instance: HostedInstanceResource) => {
        this.setState({instance});
    }

    private redirectToInstanceTasks = async () => {
        this.setState({redirectTo: routeLinks.instances.instance(this.state.instance.Id).tasks});
        return true;
    };

    private redirectToBackgroundTask = async (backgroundTaskId: string) => {
        this.setState({redirectTo: routeLinks.backgroundTasks.task(backgroundTaskId).root});
        return true;
    };

    private handleDeactivateConfirm = async () => {
        return this.doBusyTask(async () => {
            await repository.HostedInstances.deactivate(this.state.instance);
            await this.redirectToInstanceTasks();
            return true;
        });
    };

    private extend = async (): Promise<boolean> => {
        return this.doBusyTask(async () => {
            await repository.HostedInstances.extendLifetime(this.state.instance);
            await this.redirectToInstanceTasks();
        });
    }

    private reactivate = async (): Promise<boolean> => {
        return this.doBusyTask(async () => {
            await repository.HostedInstances.reactivate(this.state.instance);
            await this.redirectToInstanceTasks();
        });
    }

    private reconfigureDeactivatedInstanceResources = async (): Promise<boolean> => {
        return this.doBusyTask(async () => {
            await repository.HostedInstances.reconfigureDeactivatedInstanceResources(this.state.instance);
            await this.redirectToInstanceTasks();
        })
    }

    private revokeUsersSessions = async (): Promise<boolean> => {
        return this.doBusyTask(async () => {
            await repository.HostedInstances.revokeUsersSessions(this.state.instance);
            await this.redirectToInstanceTasks();
        });
    }

    private cancelDatabaseMaintenanceTasks = async (): Promise<boolean> => {
        return this.doBusyTask(async () => {
            await repository.HostedInstances.cancelDatabaseMaintenance(this.state.instance);
            await this.redirectToInstanceTasks();
        });
    }

    private azureAuth = async (): Promise<boolean> => {
        return this.doBusyTask(async () => {
            await repository.HostedInstances.azureAuth(this.state.instance);
            await this.redirectToInstanceTasks();
        });
    }

    private addUserAsAdmin = async (): Promise<boolean> => {
        return this.doBusyTask(async () => {
            await repository.HostedInstances.adminUser(this.state.instance);
            await this.redirectToInstanceTasks();
        });
    }


    private areYouSureYouWantToIncreaseTheDatabaseLevel() {
        return <Callout type="warning" title="Database Service Level">
            <p>
                We should only increase the database service level if the customer is experiencing consistent slowness and/or Sql timeouts.
                This should be used sparingly on Free licenses.
            </p>

            <p>
                The following questions should help you make the right decision

                . Increase the limit only when you can answer <strong>Yes</strong> to all questions:
            </p>

            <p>
                - The Seq logs show Sql timeout error messages?
            </p>
            <p>
                - The Seq logs show slow response times on API calls?
            </p>

            If you are not sure how to answer the questions then please reach out to <a rel="noopener" target="_blank" href="https://octopusdeploy.slack.com/archives/C01Q95KPRM4">#cloud-platform-requests</a> for help.

            <p>
                If you are needing to use this feature because the automated scaling is not working please contact <a rel="noopener" target="_blank" href="https://octopusdeploy.slack.com/archives/C01Q95KPRM4">#cloud-platform-requests</a>.
            </p>
        </Callout>;
    }

    private isLegacyAuthProviders(authProviders: string[]) {
        const legacyProviders = ["UsernamePassword", "AzureAD", "GoogleApps", "Okta", "OctopusID", "Guest"];
        const nonLegacyProviders = ["OctopusID", "Guest"]
        if (this.arrayEqual(authProviders, legacyProviders))
            return true;

        if (this.arrayEqual(authProviders, nonLegacyProviders))
            return false;

        return null;
    }

    private arrayEqual(arr1: string[], arr2: string[]) {
        const arr2Sorted = arr2.slice().sort();
        return arr1.length === arr2.length && arr1.slice().sort().every(function(value, index) {
            return value === arr2Sorted[index];
        });
    }

    private sortIpPrefixes(prefixes: string[]) {
        return [...prefixes].sort((a, b) => {
            const [ipA, prefixA] = a.split("/");
            const [ipB, prefixB] = b.split("/");

            if (!ipA || !prefixA || !ipB || !prefixB) {
                return a.localeCompare(b);
            }

            const numA = ipToNumber(ipA);
            const numB = ipToNumber(ipB);

            if (numA !== numB) {
                return numA - numB;
            }

            return parseInt(prefixA, 10) - parseInt(prefixB, 10);
        });

        function ipToNumber(ip: string) {
            return ip.split(".").reduce((acc, octet) => acc * 256 + parseInt(octet, 10), 0);
        }
    }

    private getCloudSubscriptionActions(limit: InstanceLimitResource) {
        return [
            <ExternalLink href={externalSystemLinks.billing.subscriptionDetails(limit)}>
                Details
            </ExternalLink>,
            this.state.environment === EnvironmentName.Production && (<ExternalLink href={externalSystemLinks.snowflake.licenseUsage(limit)}>Usage</ExternalLink>),
            this.state.environment === EnvironmentName.Production && (<ExternalLink href={externalSystemLinks.snowflake.hostingCost()}>Hosting Cost</ExternalLink>)
        ].filter(action => !!action);
    }
}
